import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["aria-labelledby", "buttons", "children", "className", "columns", "disabled", "fwdRef", "invalid", "size", "wrap"],
  _excluded2 = ["aria-required", "required"];
import classNames from 'classnames';
import React, { createContext, forwardRef, useContext, useEffect } from 'react';
import { buildVersionedClassNames } from '@op/opds.opux-utilities';
import { FormContext } from '../form/form';
import { FormRowContext } from '../form-row/form-row';
// Add alias for merging labels, since classNames does the job well:
const mergeLabels = classNames;

/**
 * _A group of options that allows users to make multiple selections from._
 *
 * ***
 *
 * The component is a group of checkboxes and offers props to organize and
 * display them in different ways. Contains [`<Checkbox>`](#/Forms/Checkbox)
 * components as its children.
 *
 * Rendered with similar styling as the [RadioGroup](#/Forms/RadioGroup) but
 * does not provide `value` and `onChange` handlers as each checkbox is an
 * independent input element. Additional props (including `ref`) will be forwarded
 * to the wrapping `<fieldset>` element.
 */
export function CheckboxGroup(_ref) {
  let {
      'aria-labelledby': prevLabels,
      buttons = false,
      children,
      className,
      columns = 1,
      disabled = false,
      fwdRef,
      invalid = false,
      size: propSize,
      wrap = false
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    spacing
  } = useContext(FormContext);
  const {
    labelId,
    onGroup,
    onGroupItems
  } = useContext(FormRowContext);
  const labels = mergeLabels(prevLabels, labelId);
  const size = propSize != null ? propSize : spacing;
  const gcn = buildVersionedClassNames('ds-checkbox-group', className, {
    'ds-checkbox-group--buttonized': buttons && !wrap,
    'ds-checkbox-group--columned': columns > 1,
    'ds-checkbox-group--wrapped': wrap,
    ["ds-checkbox-group--" + size]: size !== 'default'
  });
  // neither fieldset nor role="group" support required and therefore aria-required so destructure them away
  const restProps = _objectWithoutPropertiesLoose(props, _excluded2);
  useEffect(() => {
    if (onGroup) {
      onGroup();
    }
  }, [onGroup]);
  useEffect(() => {
    if (onGroupItems) {
      onGroupItems();
    }
  }, [onGroupItems]);
  return /*#__PURE__*/React.createElement("fieldset", _extends({
    "aria-labelledby": prevLabels ? labels : undefined,
    className: gcn,
    ref: fwdRef,
    role: labels ? undefined : 'group'
  }, restProps), /*#__PURE__*/React.createElement(CheckboxGroupContext.Provider, {
    value: {
      buttons: buttons && !wrap,
      columns,
      disabled,
      invalid,
      size,
      wrap
    }
  }, children));
}
const Fwd = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/React.createElement(CheckboxGroup, _extends({}, props, {
  fwdRef: ref
})));
Fwd.displayName = 'CheckboxGroup';
export default Fwd;
export const CheckboxGroupContext = /*#__PURE__*/createContext({
  buttons: false,
  columns: 1,
  disabled: false,
  invalid: false,
  size: 'default',
  wrap: false
});